import { useCallback, useEffect, useRef, useState } from 'react'
import type { PointerEvent as ReactPointerEvent } from 'react'
import type { Asset } from '../types'

/**
 * Press and sweep across the grid to select.
 *
 * Picking forty images one click at a time is the slowest part of building an
 * audit, and they are almost always next to each other. Holding the button and
 * running the pointer across them is how every file manager has worked for
 * thirty years.
 *
 * Two rules keep it from being annoying:
 *
 * - **The first card decides the verb.** Starting on an unselected image
 *   selects everything swept over; starting on a selected one deselects. So a
 *   sweep never undoes its own work halfway, which is what happens if each card
 *   simply toggles.
 * - **A click is still a click.** Nothing happens until the pointer has moved a
 *   few pixels, so the ordinary tap on one image is untouched — and because the
 *   sweep has already handled the card it began on, the click that follows it
 *   is swallowed rather than toggling that card straight back.
 *
 * Generic over what is being picked: library assets by default, and the
 * Amazon Carousel's results, which are not assets until they are kept.
 *
 * Mouse only. On a touch screen the same gesture is how you scroll, and there
 * is no way to tell the two apart until it is too late to give the scroll back.
 */
export function usePaintSelection<T = Asset>(
  onPaint?: (item: T, select: boolean) => void,
  /**
   * Only with Shift held -- in the Miro panel, where a plain press-and-move
   * drags a picture onto the board (Huanrui, 2026-10-02).
   */
  needsShift = false,
) {
  const painting = useRef(false)
  const mode = useRef(true)
  const startedOn = useRef<T | null>(null)
  const moved = useRef(false)
  const [active, setActive] = useState(false)

  useEffect(() => {
    if (!onPaint) return
    const stop = () => {
      painting.current = false
      setActive(false)
    }
    window.addEventListener('pointerup', stop)
    window.addEventListener('pointercancel', stop)
    return () => {
      window.removeEventListener('pointerup', stop)
      window.removeEventListener('pointercancel', stop)
    }
  }, [onPaint])

  const onPointerDown = useCallback(
    (asset: T, isSelected: boolean, event: ReactPointerEvent) => {
      if (!onPaint || event.pointerType !== 'mouse' || event.button !== 0) return
      if (needsShift && !event.shiftKey) return
      painting.current = true
      mode.current = !isSelected
      startedOn.current = asset
      moved.current = false
    },
    [onPaint, needsShift],
  )

  const onPointerEnter = useCallback(
    (asset: T, isSelected: boolean) => {
      if (!onPaint || !painting.current) return
      // The sweep has left the card it began on, so this is a drag and not a
      // click. From here the card underneath the pointer is painted.
      if (!moved.current) {
        moved.current = true
        setActive(true)
        // The card the sweep began on never got a click, so it is painted here.
        if (startedOn.current) onPaint(startedOn.current, mode.current)
      }
      if (isSelected !== mode.current) onPaint(asset, mode.current)
    },
    [onPaint],
  )

  /** True once the card the sweep started on has already been dealt with. */
  const shouldSwallowClick = useCallback(() => {
    const swallow = moved.current
    moved.current = false
    startedOn.current = null
    return swallow
  }, [])

  return { onPointerDown, onPointerEnter, shouldSwallowClick, active }
}
